import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  AddressElement,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import {
  CreatePaymentMethodData,
  StripeAddressElementChangeEvent
} from '@stripe/stripe-js';
import { useSnackbar } from 'notistack';
import React, { FormEvent, useEffect, useMemo, useState } from 'react';

import { useAuthContext } from 'src/auth/hooks';
import { useCheckout } from './useCheckout';
import { SubscriptionDialog } from '../SubscriptionDialog';
import { ChoosePlan, CheckoutSummary, PaymentMethods } from './index';
import { useApiRepo, Endpoints } from '../../utils/Api';
import { Colors } from '../../utils/Colors';
import { hoverCss, selectedCss } from '../../utils/Styles';
import { ICheckSubscription } from '../../views/Stripe';
import { Price, Subscription } from './types';
import './CheckoutForm.css';

export const CheckoutForm = ({ checkSubscription }: ICheckSubscription) => {
  const { user } = useAuthContext();
  const { getObjectsFromAPI } = useApiRepo();
  const stripe = useStripe();
  const elements = useElements();

  const {
    isError,
    isLoading,
    isSuccess,
    message,
    open,
    subscriptionStatus,
    toError,
    toLoading,
    toLoadingDone,
    toReset,
    toSuccess
  } = useCheckout();
  const { enqueueSnackbar } = useSnackbar();
  const [subscription, setSubscription] = useState<Subscription>();
  const [paymentMethodList, setPaymentMethodList] = useState<any[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<string | undefined>(
    undefined
  );
  const [accordion, setAccordion] = useState<'PREVIOUS' | 'NEW' | 'NONE'>(
    'NEW'
  );
  const [addressIsCompleted, setAddressIsCompleted] = useState<boolean>(false);
  const [displayRow, setDisplayRow] = useState(window.innerWidth > 1150);
  const [prices, setPrices] = useState<Price[]>([]);
  const YEARLY_ID = useMemo(
    () =>
      prices && prices.length > 0
        ? prices.find((price) => price.recurring.interval === 'year')?.id
        : undefined,
    [prices]
  );
  const [priceId, setPriceId] = useState(YEARLY_ID);
  useEffect(() => {
    if (subscription && subscription.plan) setPriceId(subscription.plan.id);
    else setPriceId(YEARLY_ID);
  }, [YEARLY_ID, subscription]);
  window.addEventListener('resize', () =>
    setDisplayRow(window.innerWidth > 1150)
  );

  const planSelect = async (id: string) => {
    setPriceId(id);
  };

  const cancelSubscription = async () => {
    toLoading();
    const api = await getObjectsFromAPI();
    await api.delete(Endpoints.cancelSubscription);
    checkSubscription();
    toSuccess('cancelled');
  };

  useEffect(() => {
    const fetchedData = async () => {
      const api = await getObjectsFromAPI();
      toLoading();
      const { data: priceData } = await api.get(Endpoints.prices);
      setPrices(priceData.prices.data);
      const {
        data: { paymentMethods }
      } = await api.get(Endpoints.paymentMethods);
      setPaymentMethodList(paymentMethods);
      const defaultMethod = paymentMethods.find(
        (method: any) => method.isDefault
      );
      if (defaultMethod) setPaymentMethod(defaultMethod.id);
      setAccordion('PREVIOUS');
      toLoadingDone();
    };
    if (!isLoading) fetchedData();
  }, [isSuccess]);

  useEffect(() => {
    const fetchSubscription = async () => {
      toLoading();
      const api = await getObjectsFromAPI();
      const { data } = await api.get(Endpoints.fetchSubscription);
      setSubscription(data.subscription);
      toLoadingDone();
    };
    if (!isLoading) fetchSubscription();
  }, [isSuccess]);

  const deletePaymentMethod = async (id: string) => {
    toLoading();
    const api = await getObjectsFromAPI();
    await api.delete(`${Endpoints.removePaymentMethod}${id}`);
    toSuccess('initial');
  };

  const submitWithNewPayment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stripe || !elements || !user) {
      // Stripe.js has not loaded yet
      return;
    }

    const cardElement = elements.getElement(CardElement);
    const address = elements.getElement(AddressElement);
    const addressObj = address
      ? await address.getValue()
      : { isNewAddress: false, value: {} };
    if (
      'address' in addressObj.value &&
      addressObj.value.address.country !== 'US'
    ) {
      toError(
        'Currently, we only support US addresses. Please sign up in the link above for updates on when we are available in other regions'
      );
      return;
    }

    // Create a payment method
    if (cardElement) {
      let paymentMethodBody: CreatePaymentMethodData = {
        type: 'card',
        card: cardElement
      };
      if ('name' in addressObj.value && 'address' in addressObj.value) {
        paymentMethodBody = {
          ...paymentMethodBody,
          billing_details:
            addressObj.value as CreatePaymentMethodData['billing_details']
        };
      }
      const { error, paymentMethod: newPaymentMethod } =
        await stripe.createPaymentMethod(paymentMethodBody);

      if (error) {
        if (error.message) toError(error.message);
        // eslint-disable-next-line no-console
        console.error(error);
        return;
      }

      // Send payment information to your backend
      try {
        toLoading();
        const api = await getObjectsFromAPI();
        const { data } = await api.post(Endpoints.createCustomer, {
          email: user.email,
          isNewAddress: addressObj.isNewAddress,
          ...addressObj.value
        });

        await api.post(
          Endpoints.createSubscription,
          {
            customer_id: data.customer_id,
            price_id: priceId,
            payment_method_id: newPaymentMethod.id
          },
          { timeout: 0 }
        );

        toSuccess(
          subscription && Object.keys(subscription).length > 0
            ? 'updated'
            : 'new'
        );
        checkSubscription();
      } catch (err) {
        toError('Subscription failed. Please try again.');
        throw err;
      }
    }
  };

  const addressOnChange = (event: StripeAddressElementChangeEvent) => {
    setAddressIsCompleted(event.complete);
  };

  const submitWithExistingPaymentMethod = async (
    event: FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    if (!stripe || !elements || !user) {
      // Stripe.js has not loaded yet
      return;
    }
    const method = paymentMethodList.find((pm) => pm.id === paymentMethod);
    try {
      toLoading();
      const api = await getObjectsFromAPI();
      await api.post(
        Endpoints.createSubscription,
        {
          customer_id: method.customer,
          price_id: priceId,
          payment_method_id: method.id
        },
        { timeout: 0 }
      );
      toSuccess(
        subscription && Object.keys(subscription).length > 0 ? 'updated' : 'new'
      );
      checkSubscription();
    } catch (err) {
      toError('Subscription failed. Please try again.');
      throw err;
    }
  };

  const subscriptionText = useMemo(() => {
    if (subscription?.status === 'active') {
      return ' current';
    }
    if (
      subscription?.ended_at &&
      subscription?.current_period_end &&
      subscription.current_period_end * 1000 > Date.now()
    ) {
      return 'n expiring';
    }
    return 'n invalid';
  }, [subscription]);

  const handleSubscription = async (event: FormEvent<HTMLFormElement>) => {
    if (paymentMethod === '') submitWithNewPayment(event);
    else if (paymentMethod !== undefined)
      submitWithExistingPaymentMethod(event);
  };

  const subscriptionSubmitText = useMemo(() => {
    if (isLoading) return 'Processing...';
    if (subscription && subscription?.status) return 'Update Subscription';
    return 'Subscribe';
  }, [isLoading, subscription]);

  useEffect(() => {
    if (message && isError) enqueueSnackbar(message, { variant: 'error' });
  }, [isError, message]);

  const defaultMethod = useMemo(
    () =>
      paymentMethodList
        ? paymentMethodList.find((method: any) => method.isDefault)?.id
        : '',
    [paymentMethodList]
  );

  return (
    <Grid container direction={displayRow ? 'row' : 'column'} wrap="nowrap">
      <Grid
        item
        xs={4}
        sx={{
          borderRight: `1px solid ${Colors.lightGray}`
        }}
      >
        {subscription ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            paddingLeft={6}
            paddingTop="2rem"
          >
            <Typography
              sx={{ marginBottom: '.5rem' }}
              variant="h5"
              fontWeight={700}
            >
              You have a{subscriptionText} subscription
            </Typography>
            <Button
              disableElevation
              variant="contained"
              style={{
                backgroundColor: Colors.lightGray,
                color: 'black',
                marginRight: 'auto'
              }}
              onClick={() => toSuccess('display')}
            >
              View
            </Button>
          </Box>
        ) : null}
        <ChoosePlan
          defaultPriceId={priceId}
          onChange={planSelect}
          prices={prices}
        />
      </Grid>
      <Grid item xs={4}>
        <Box
          sx={{
            margin: 'auto',
            marginTop: '25px',
            paddingTop: '5px',
            width: '90%'
          }}
        >
          <Typography variant="h5" fontWeight="700">
            Notice
          </Typography>
          <Typography variant="body1" marginBottom="2rem">
            OCDfeat is currently available for US residents only. If you reside
            outside the US, please{' '}
            <a
              className="sign-up-link"
              href="https://share.hsforms.com/1UAGjnOsqQKS3AkTlIb1WEw8naos"
            >
              Sign Up
            </a>{' '}
            to be Notified when the app will be available in your area. Thank
            you!
          </Typography>
          <Typography variant="h5" fontWeight="700">
            Payment Details
          </Typography>
          <form id="payment-form" onSubmit={handleSubscription}>
            <Grid container direction="column">
              {paymentMethodList.length > 0 ? (
                <Accordion
                  expanded={accordion === 'PREVIOUS'}
                  onChange={() => {
                    setAccordion((currentStatus) =>
                      currentStatus === 'PREVIOUS' ? 'NONE' : 'PREVIOUS'
                    );
                    setPaymentMethod(defaultMethod);
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography padding={2}>
                      Existing Payment Methods
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <PaymentMethods
                      defaultMethod={defaultMethod}
                      deleteMethod={deletePaymentMethod}
                      paymentMethodList={paymentMethodList}
                      methodOnClick={(id: string | undefined) =>
                        setPaymentMethod(id)
                      }
                      selectedPayment={paymentMethod}
                    />
                  </AccordionDetails>
                </Accordion>
              ) : null}
              <Accordion
                expanded={accordion === 'NEW'}
                onChange={() => {
                  setAccordion((currentStatus) =>
                    currentStatus === 'NEW' ? 'NONE' : 'NEW'
                  );
                  setPaymentMethod(undefined);
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography padding={2}>New Payment Method</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid item padding={2}>
                    <Card
                      variant="outlined"
                      onClick={() => setPaymentMethod('')}
                      sx={
                        paymentMethod === ''
                          ? {
                              ...selectedCss,
                              ...hoverCss
                            }
                          : {
                              ...hoverCss
                            }
                      }
                    >
                      <CardContent>
                        <div className="card-element-container">
                          <CardElement
                            id="card-element"
                            onChange={() => setPaymentMethod('')}
                            options={{
                              style: {
                                base: {
                                  backgroundColor: '#F6F8FA',
                                  color: '#262626',
                                  lineHeight: '50px',
                                  fontFamily: ' "Gill Sans", sans-serif',
                                  fontWeight: '500'
                                }
                              }
                            }}
                          />
                        </div>
                        <Typography
                          variant="subtitle2"
                          sx={{ paddingTop: 4, paddingBottom: 1 }}
                        >
                          Billing Address
                        </Typography>
                        <AddressElement
                          onChange={addressOnChange}
                          options={{ mode: 'billing' }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Box display="flex">
              <Button
                disabled={
                  !stripe ||
                  isLoading ||
                  priceId === '' ||
                  paymentMethod === undefined ||
                  (paymentMethod === undefined && !addressIsCompleted)
                }
                type="submit"
                disableElevation
                variant="contained"
                sx={{
                  backgroundColor: Colors.lightGray,
                  color: 'black',
                  fontWeight: 700,
                  marginTop: '10px'
                }}
              >
                {subscriptionSubmitText}
              </Button>
              {isLoading ? (
                <CircularProgress
                  sx={{
                    color: Colors.ocdGreen,
                    marginTop: '10px',
                    marginLeft: '10px'
                  }}
                />
              ) : null}
            </Box>
          </form>
        </Box>
      </Grid>
      <Grid item xs={4}>
        <CheckoutSummary
          prices={prices}
          priceId={priceId}
          borderTop={!displayRow}
        />
      </Grid>
      {subscription ? (
        <SubscriptionDialog
          close={toReset}
          onCancel={cancelSubscription}
          open={open}
          subscription={subscription}
          subscriptionStatus={subscriptionStatus}
        />
      ) : null}
    </Grid>
  );
};
